/**
 * カード — 官位（かんい）8枚
 *
 * docs/01-ゲームデザイン.md §9 の「官位 8枚」に対応する。
 * 官位札は京（上洛）で購入でき、威信と天下点（vp）を持つ。
 *
 * 【設計方針】
 * 官位は「戦って取るもの」ではなく「朝廷・幕府から与えられるもの」である。
 * したがって効果の中心は威信（ishin）と天下点（vp）であり、
 * 兵や役の段階を直接上げる札は原則として置かない。
 * 例外は関東管領（名分を得て他国へ出兵した実例がある）だけで、
 * これも formationMod を +1 一段にとどめてある。
 *
 * 「サイコロ+1」のような旧仕様の効果は書かない。
 * 合戦は docs/06-賭けと軍配勝負.md のとおりテキサスホールデムで解決するため、
 * timing: 'battle' の札を置く場合は formationMod / holeCardMod で効かせる。
 * ただし本ファイルには timing:'battle' の札は無い（官位は評定・常時の札である）。
 *
 * 【この8枚の内訳】
 *   朝廷の官（自称・下賜を含む）  受領名 ・ 関白 ・ 太政大臣
 *   幕府の職（在地の統治）        守護代 ・ 守護職
 *   幕府の職（広域の出先）        探題 ・ 関東管領
 *   武家の棟梁                    征夷大将軍
 *
 * この並びは、そのまま「武家官位」という制度の見取り図になっている。
 * 戦国大名が求めた官位の多くは朝廷の官職名であり、
 * 幕府の職（守護・探題・管領・将軍）とは系統が異なる。
 * 図鑑でこの二系統が見分けられるよう、keywords に系統名を入れてある。
 *
 * 【執筆基準】docs/05-学習設計.md §4 と provinces/_reference.ts に揃える。
 *   - 武将を「英雄」「悪」と評価しない。戦闘行為を美化・賛美しない
 *   - 宗教勢力の扱いは事実の記述に留める
 *   - 女性・民衆の存在を消さない。献金・段銭・乱取りなど負担の側も書く
 *   - 通説と異説がある事項は caveat に必ず書く
 *   - ゲームの都合で史実を改変した箇所は gameNote に必ず書く
 *   - 年号は西暦を主とし、必要に応じて和暦を添える
 *
 * 【征夷大将軍の解禁と廃止について】
 * docs/01 §7 の第III章の表に「1573 室町幕府滅亡 → 時代法：『将軍』カード全廃」がある。
 * したがって SEII_TAISHOUGUN は availableFrom: 1 だが、1573年の時代法で山札から除かれる。
 * この「途中で札が消える」こと自体が学習設計の一部である（docs/05 §1）。
 * ただし足利義昭が将軍職を辞したのは1588年であり、
 * 「1573年に幕府が滅んだ」という区切りが便宜的なものであることは gameNote に書いた。
 */

import type { Card } from '../types';

// ---------------------------------------------------------------------------
// 朝廷の官 — 名乗り
// ---------------------------------------------------------------------------

/** もっとも安い官位札。自称・下賜を含む「名乗り」の札。 */
export const ZURYOUMEI: Card = {
  id: 'card:kani:zuryoumei',
  name: '受領名',
  reading: 'ずりょうめい',
  type: 'kani',
  cost: { zeni: 2 },
  timing: 'anytime',
  effects: [{ t: 'gain', res: 'ishin', n: 1 }],
  vp: 1,
  availableFrom: 1,
  note: {
    summary:
      '「上総介」「三河守」のような国司の官職名。本来は朝廷が任じるものだが、戦国期には自称する例や、大名が家臣に与える例が広まった。',
    detail: {
      1: '「上総介（かずさのすけ）」「三河守（みかわのかみ）」のような、むかしの役所の名前です。ほんとうは朝廷がきめるものでしたが、戦国のころには自分で名のる人がふえ、大名が家来にごほうびとしてあたえることもありました。名前で身分の高さをしめしたのです。',
      2: '「上総介」「三河守」など国司の官職名を受領名、「修理大夫」「左衛門尉」など中央の官職名を官途名という。本来は朝廷が任じるものだが、戦国期には朝廷や幕府への働きかけで得る例、単に自称する例、大名が官途状・受領書出を発給して家臣に名乗らせる例が広まった。織田信長は元服後まもなく「上総守」と署名し、のちに「上総介」へ改めている。上総は親王が太守を務める親王任国で、現地の長官が介であったためとみられる。村の名主が「〜兵衛」「〜左衛門」と名乗るのも、官職名が広く行き渡った結果である。',
      3: '受領名は「上総介」「三河守」といった国司の官職名、官途名は「修理大夫」「左衛門尉」といった中央官司の官職名を指し、あわせて武家官位と呼ばれる制度の裾野をなす。律令の建前では朝廷が任命するものだが、戦国期には三つの経路が並存した。第一に朝廷や幕府への献金・取次によって正式な口宣案を得るもの、第二に何の手続きも経ずに名乗る僭称、第三に大名が官途状・受領書出を発給して家臣の名乗りを認めるものである。大友宗麟が家臣を右馬助に任じた官途状のように、大名が朝廷の権限を事実上代行した例が残る。織田信長は元服後まもなく「上総守」と署名し、のちに「上総介」に改めた。上総・上野・常陸は親王が太守に任じられる親王任国で、実際に現地を統べるのは介であったから、これを踏まえた訂正とみられている。官職名は武士の外にも広がり、村の名主や職人が「〜兵衛」「〜左衛門」と名乗る百官名・東百官が定着した。近世の庶民の名の多くはこの流れを引いており、官位は大名だけの話ではなく、人の呼ばれ方そのものを形づくった制度だった。',
    },
    year: [1467, 1600],
    keywords: ['受領名', '官途名', '武家官位', '朝廷の官', '官途状', '百官名'],
    caveat:
      '史料に見える官職名が正式な任官によるものか自称かを区別するのは難しい場合が多く、「自称だった」と断定できる例は限られます。信長が「上総守」から「上総介」へ改めた理由も、署名の変化からの推測であり、本人がそう述べた史料はありません。',
    gameNote:
      '盤上では銭を払えば必ず手に入りますが、実際には朝廷への献金、幕府への取次、あるいは自称と、経路も費用もさまざまでした。一律の値段にしたのは遊びやすさのためです。',
  },
};

// ---------------------------------------------------------------------------
// 幕府の職 — 在地の統治
// ---------------------------------------------------------------------------

/** 守護の代官。ここから実権を握った家が多い。下剋上札を伴う。 */
export const SHUGODAI: Card = {
  id: 'card:kani:shugodai',
  name: '守護代',
  reading: 'しゅごだい',
  type: 'kani',
  cost: { zeni: 3 },
  timing: 'council',
  effects: [
    { t: 'gain', res: 'ishin', n: 1 },
    { t: 'grantGekokujo', n: 1 },
  ],
  vp: 1,
  availableFrom: 1,
  note: {
    summary:
      '守護に代わって任国を治める役。守護が京に住むことが多かったため、在地の実務と兵をにぎり、やがて主家をしのぐ家が各地に現れた。',
    detail: {
      1: '守護は京に住むことが多く、国の仕事は「守護代」という代わりの人がしました。国の田畑や人のことをよく知り、兵もあつめられたので、だんだん力をつけ、主人よりつよくなる家も出てきました。越後の長尾氏や尾張の織田氏がその例です。',
      2: '守護代は、守護に代わって任国の統治を行う役職である。室町幕府の守護は在京して幕政に加わることが多く、任国の実務は現地に置いた守護代が担った。守護代は年貢や段銭の徴収、国人の統率、軍勢の動員といった実務を握るため、在地での力は守護本人より強くなりやすい。応仁の乱以降、越後守護代の長尾氏、尾張守護代の織田氏、出雲守護代の尼子氏のように、守護代の家が主家を退けて事実上の国主となる例が各地に現れた。織田信長はその守護代家からさらに分かれた弾正忠家の出身である。',
      3: '守護代は守護の被官のうち、任国の統治を委ねられた者を指す。室町幕府の守護は在京して幕政に参与するのが原則であったため、年貢・段銭の徴収、国人層の統率、軍勢の動員、検断といった実務は在地の守護代が担った。この構造は必然的に、在地での実力が守護本人より守護代に集まる結果を生む。応仁の乱以降、越後守護代長尾氏、尾張守護代織田氏、出雲守護代尼子氏などが主家をしのぎ、事実上の国主となった。織田信長はさらにその守護代家から分かれた清洲三奉行の一家（弾正忠家）の出身で、家格としては低い位置から出発している。ただし、こうした過程を一律に「下剋上」と括ることには研究上の留保がある。守護と守護代の関係は地域によって大きく異なり、守護を追放した例、守護を名目上戴き続けた例、守護家の内紛に乗じて権限を吸収した例が併存する。また守護代自身も配下の国人に押し上げられ、あるいは突き上げられる存在であり、単純な主従の逆転として描けるものではない。長尾氏が上杉憲政から山内上杉家の家督を譲られて「上杉」を名乗ったように、権威そのものを継承する形をとる例もあった。',
    },
    year: [1467, 1560],
    keywords: ['守護代', '幕府の職', '長尾氏', '織田氏', '尼子氏', '下剋上'],
    caveat:
      '守護代の台頭をすべて「下剋上」と一括りにすることはできません。守護を追った例、名目上は守護を戴き続けた例、内紛に乗じて権限だけを吸収した例など実態は地域差が大きく、近年は単線的な下剋上像が見直されています。',
    gameNote:
      '盤上ではこの札に下剋上札が付いてきますが、これは docs/06 §6 の逆転装置を史実の語感に重ねたものです。守護代であれば必ず主家を倒せた、という意味ではありません。',
  },
};

/** 国ごとに置かれた幕府の職。半済・段銭の徴収権を石高に置き換えている。 */
export const SHUGOSHIKI: Card = {
  id: 'card:kani:shugoshiki',
  name: '守護職',
  reading: 'しゅごしき',
  type: 'kani',
  cost: { zeni: 4 },
  timing: 'council',
  effects: [
    { t: 'gain', res: 'ishin', n: 2 },
    { t: 'gain', res: 'koku', n: 2 },
  ],
  vp: 2,
  availableFrom: 1,
  note: {
    summary:
      '室町幕府が国ごとに置いた職。軍事・警察の権限だけでなく、年貢の半分を取る権利（半済）や段銭の徴収も認められ、国を治める根拠となった。',
    detail: {
      1: '守護は、幕府が国ごとにおいた役です。もとは犯人をつかまえたり兵をあつめたりする役でしたが、しだいに年貢の半分を取る権利などももち、国を治める人になっていきました。段銭は村の人たちが出すお金で、その負担は重いものでした。',
      2: '守護は室町幕府が国ごとに置いた職で、軍事・警察の権限（大犯三箇条）に加え、年貢の半分を取得できる半済や、臨時に課す段銭の徴収権を得て、任国支配の根拠となった。応仁の乱の後、守護は在京をやめて領国へ下る者と、守護代や国人に取って代わられる者に分かれる。1522年（大永2年）には伊達稙宗が陸奥守護に任じられており、幕府の家格の枠外にいた家が守護職によって大名として遇された例もある。段銭や兵糧米は最終的に村々が負担したため、守護の権限の拡大は百姓の負担の増加と表裏だった。',
      3: '守護は室町幕府が一国ごとに置いた職で、鎌倉期の大犯三箇条（大番催促・謀叛人および殺害人の検断）を基本としつつ、南北朝期以降に刈田狼藉の検断、使節遵行、半済（年貢の半分を軍費に充てる権限）、段銭の徴収などを重ねて獲得し、任国支配の法的根拠となった。応仁の乱以降、守護の進路は分かれる。今川・武田・大内・島津のように守護家がそのまま領国支配を強めた例、越後上杉・尾張斯波のように守護代に実権を奪われた例、六角・畠山のように家中の分裂で衰えた例がある。1522年（大永2年）、伊達稙宗が陸奥守護に補任されたのは幕府にとって前例のない措置で、稙宗が望んだ奥州探題は足利一門でないことを理由に認められず、代わりに守護職を与えて「国人」から「大名」への格上げを図ったものと解されている。守護職の権限のうち半済と段銭は、いずれも荘園領主の得分と百姓の負担に直接触れるものであり、権限の拡大は在地の紛争を増やした。守護請（荘園の年貢納入を守護が請け負う仕組み）を通じて荘園制が変質していく過程も、この延長線上にある。なお「守護大名がそのまま戦国大名になった」という単線的な理解は現在では取られず、戦国大名の成立経路は守護・守護代・国人・幕府奉公衆と多様であったとされる。',
    },
    year: [1467, 1590],
    keywords: ['守護職', '幕府の職', '半済', '段銭', '守護請', '伊達稙宗'],
    caveat:
      '伊達稙宗の陸奥守護補任について「本人は奥州探題を望んだが一門でないため守護職で代替された」という説明は、幕府側の意図を史料から推定したものであり、確定した事実ではありません。また守護大名がそのまま戦国大名になったという理解も、現在では単純すぎるとされています。',
    gameNote:
      '盤上では石高が増える形にしていますが、守護職そのものが米を生むわけではありません。半済と段銭の徴収権を持つことを、資源の増加に置き換えた処理です。実際にその分を負担したのは荘園領主と村々でした。',
  },
};

// ---------------------------------------------------------------------------
// 幕府の職 — 広域の出先
// ---------------------------------------------------------------------------

/** 九州探題・奥州探題など、広域を束ねる幕府の出先。 */
export const TANDAI: Card = {
  id: 'card:kani:tandai',
  name: '探題',
  reading: 'たんだい',
  type: 'kani',
  cost: { zeni: 5 },
  timing: 'council',
  effects: [
    { t: 'gain', res: 'ishin', n: 2 },
    { t: 'gain', res: 'gunryaku', n: 2 },
  ],
  vp: 2,
  availableFrom: 2,
  note: {
    summary:
      '幕府が広い地域を束ねるために置いた出先の職。九州探題・奥州探題・羽州探題があり、原則として足利一門が任じられたが、戦国期には献金によって得る例も出た。',
    detail: {
      1: '探題は、幕府がひろい地域をまとめるためにおいた役です。九州や奥州（東北）におかれました。もとは将軍の一族がなる決まりでしたが、戦国のころには幕府にたくさんのお金をおくって、この役をもらう大名が出てきました。',
      2: '探題は室町幕府が広域統治のために置いた出先の職で、九州探題・奥州探題・羽州探題があった。原則として足利一門が任じられ、九州探題は渋川氏、奥州探題は大崎氏、羽州探題は最上氏が世襲した。しかし戦国期には幕府の財政難を背景に、献金と引き換えに補任される例が現れる。1555年（天文24年／弘治元年）に伊達晴宗が奥州探題に任じられて大崎氏から交替し、1559年（永禄2年）には大友義鎮（宗麟）が足利義輝への多額の献上を経て、6月に豊前・筑前の守護、11月に九州探題に任じられた。実際の支配力の裏付けがなくても、探題の肩書は周囲の国人を従える名分になった。',
      3: '探題は室町幕府が広域統治のために設けた出先機関で、九州探題・奥州探題・羽州探題が知られる。いずれも原則として足利一門が任じられ、九州探題は渋川氏、奥州探題は大崎氏、羽州探題は最上氏が世襲した。ところが応仁の乱後、幕府の実効支配が畿内周辺に縮小し財政も逼迫すると、探題職は献金の見返りとして授与される性格を強める。1555年、伊達晴宗が奥州探題に補任され、大崎氏の世襲が断たれた。これは伊達氏の家格上昇を示すと同時に、足利一門に限るという原則自体の放棄であり、幕府の地方統治方針の転換とみる評価がある。1559年には大友義鎮（宗麟）が足利義輝に多額の進物を送り、6月に豊前・筑前守護、11月に九州探題へ補任された。ただし補任の実質は、現地で既に確立していた支配を幕府が追認する形に近く、探題職が新たな権力を与えたわけではない。それでも大名がこれを求めたのは、周辺の国人や競合勢力に対して「幕府に認められた者」として振る舞う名分が、軍事力とは別の資源として機能したためである。幕府の側から見れば、実効性を失った職の授与は残された数少ない収入源であり、朝廷の官位授与と同じく、権威の切り売りという性格を帯びていた。',
    },
    year: [1522, 1573],
    keywords: ['探題', '幕府の職', '九州探題', '奥州探題', '大友義鎮', '伊達晴宗'],
    caveat:
      '大友義鎮の九州探題補任の年次や、伊達晴宗の奥州探題補任が持った意味については、研究者によって評価に幅があります。探題職が実際にどこまでの権限を伴ったかも、時期と地域によって大きく異なり、一律には言えません。',
    gameNote:
      '盤上では軍略が増える形にしていますが、探題の肩書が兵を動かす力を直接与えたわけではありません。「幕府に認められた者として振る舞える」という名分の効き目を、手番の選択肢の広さに置き換えた処理です。',
  },
};

/** 鎌倉公方を補佐する職。上杉謙信が継承し、関東出兵の名分となった。 */
export const KANTOU_KANREI: Card = {
  id: 'card:kani:kantou_kanrei',
  name: '関東管領',
  reading: 'かんとうかんれい',
  type: 'kani',
  cost: { zeni: 4, gunryaku: 1 },
  timing: 'council',
  effects: [
    { t: 'gain', res: 'ishin', n: 3 },
    { t: 'holeCardMod', n: 1, scope: 'attack' },
  ],
  vp: 2,
  availableFrom: 2,
  note: {
    summary:
      '鎌倉府の長である鎌倉公方を補佐する職で、上杉氏がほぼ世襲した。1561年に長尾景虎が上杉憲政から家督とともに譲られ、以後の関東出兵の名分となった。',
    detail: {
      1: 'むかし幕府は、関東をおさめるため鎌倉に代表をおきました。この代表を助ける役が関東管領で、上杉氏がつとめました。1561年、越後の長尾景虎（のちの上杉謙信）が上杉家の跡つぎとしてこの役をつぎ、上杉と名のりました。その後なんども関東へ兵を出し、通り道の村々は苦しみました。',
      2: '関東管領は、室町幕府が関東に置いた鎌倉府の長官（鎌倉公方）を補佐する職で、上杉氏がほぼ世襲した。上杉氏は山内・扇谷の二家に分かれて争い、山内上杉憲政は北条氏に敗れて越後の長尾景虎を頼る。1561年（永禄4年）閏3月、景虎は鎌倉の鶴岡八幡宮で山内上杉家の家督と関東管領職を譲られ、憲政から一字を受けて上杉政虎（のちの謙信）と名乗った。就任には将軍足利義輝の承認も得ている。以後、彼は関東管領の名分のもとで越後から関東へ繰り返し出兵した。ただし出兵は現地に宿と兵糧の負担を強い、乱取りと呼ばれる人や物の連れ去りも記録されている。',
      3: '関東管領は、室町幕府が東国統治のために置いた鎌倉府において、長官である鎌倉公方を補佐する職であり、上杉氏がほぼ世襲した。上杉氏は山内・扇谷の二家に分立して抗争し、さらに鎌倉公方が古河公方・堀越公方に分裂したため、関東の秩序は十五世紀のうちに幾重にも割れていた。山内上杉憲政は北条氏康（後北条氏、出自は伊勢氏）に押されて上野を追われ、越後守護代の家である長尾景虎を頼る。永禄4年（1561年）閏3月、景虎は鎌倉の鶴岡八幡宮において憲政から山内上杉家の家督と関東管領職を譲られ、憲政の偏諱を受けて上杉政虎と名乗った。将軍足利義輝の承認も得ており、越後の一守護代家が関東の旧秩序の頂点に立つ形式が整えられた。以後、政虎（謙信）は関東管領の名分のもとで十数度にわたり関東へ出兵する。この一連の出兵を「義のための戦い」と説明することが広く行われてきたが、これは『甲陽軍鑑』などの軍記に由来する評価であり、越後の在地領主層への恩賞の確保、関東の反北条勢力との関係、上洛路の維持など、複数の要因から検討されるべきものである。また藤木久志らの研究は、越後勢の関東出兵にともなう乱取り、すなわち人の連行と売買が同時代の記録に見えることを指摘しており、出兵は現地の村々にとって宿・兵糧の負担と身の危険を意味した。名分と実際の被害は別の水準の事柄として押さえておく必要がある。',
    },
    year: 1561,
    keywords: ['関東管領', '幕府の職', '上杉憲政', '上杉謙信', '鶴岡八幡宮', '鎌倉府'],
    caveat:
      '関東管領就任の儀式の次第は後世の編纂物に依るところが大きく、細部は確認できません。また謙信の関東出兵を「義のため」と説明する見方は軍記に由来する評価であり、動機については複数の説があります。',
    gameNote:
      '盤上では攻め手の役が1段階上がります（docs/06 §4）。これは「名分を得た側に人が集まる」ことを表した処理であって、出兵を有利で望ましいものとして推奨する意図はありません。実際の出兵は通り道の村々に重い負担を与えました。',
  },
};

// ---------------------------------------------------------------------------
// 朝廷の官 — 頂点
// ---------------------------------------------------------------------------

/** 摂関家が独占していた朝廷の最高職。1585年に羽柴秀吉が就任した。 */
export const KANPAKU: Card = {
  id: 'card:kani:kanpaku',
  name: '関白',
  reading: 'かんぱく',
  type: 'kani',
  cost: { zeni: 8 },
  timing: 'council',
  effects: [
    { t: 'gain', res: 'ishin', n: 4 },
    { t: 'target', who: 'others', then: [{ t: 'lose', res: 'gunryaku', n: 1 }] },
  ],
  vp: 4,
  availableFrom: 4,
  note: {
    summary:
      '天皇を補佐する朝廷の最高職で、代々摂関家が務めた。1585年、羽柴秀吉が近衛前久の猶子となる形で就任し、この地位を根拠に大名間の私戦を禁じる命令を出した。',
    detail: {
      1: '関白は、天皇を助けて政治をおこなういちばん上の役です。むかしから決まった五つの家しかなれませんでしたが、1585年に羽柴秀吉がその一つの家の養子になる形でこの役につきました。そして「大名どうしで勝手に戦ってはいけない」と命じました。',
      2: '関白は天皇を補佐する朝廷の最高職で、近衛・九条・二条・一条・鷹司の五摂家が独占していた。1585年（天正13年）、近衛信輔と二条昭実が関白職を争った関白相論のさなか、羽柴秀吉が近衛前久の猶子となる形で関白に任じられる。翌1586年には太政大臣となり、豊臣の姓を与えられた。秀吉は関白の地位を根拠として、大名の領地の境目争いを裁定し、私に戦うことを禁じる命令を各地へ出している。妻の北政所（おね）は1585年に従三位、1588年には従一位に叙されており、官位は男性だけのものではなかった。',
      3: '関白は天皇を補佐して政務を統べる朝廷の最高職で、平安期以来、藤原氏の嫡流である五摂家（近衛・九条・二条・一条・鷹司）が独占してきた。天正13年（1585年）、前関白近衛前久の子信輔と、現職の二条昭実との間で関白職の相続が争われた（関白相論）。この紛糾のなかで、羽柴秀吉は前久の猶子となる形式を整えて7月に関白に任じられた。翌天正14年（1586年）9月に正親町天皇から豊臣の姓を賜り、12月に太政大臣となる。秀吉が征夷大将軍ではなく関白を選んだ理由については、武家の棟梁ではなく朝廷の権威を借りて全国の大名の上に立つ枠組みを選んだとする見方が有力だが、足利義昭の猶子になろうとして拒まれたという逸話は後世の記録に由来し、史料的な裏付けを欠く。秀吉は関白の地位を根拠に、大名間の領地紛争を自らの裁定に委ねさせ、私に戦うことを禁じる命令を各地に発した。これを一括して「惣無事令」と呼ぶ理解が長く通用してきたが、単一の法令が存在した証拠はなく、個別の命令の集積とみる批判が有力である。またこの禁令は大名間に限らず、村と村の境相論や自力救済にも及んだとする研究があり、在地の紛争解決のあり方そのものを変えていく過程として論じられている。秀吉の妻である北政所（おね）は天正13年に従三位となり北政所の称を得、聚楽第への後陽成天皇行幸を差配した功により天正16年（1588年）に従一位に叙された。生前に従一位を受けた女性は少なく、官位が男性だけの制度ではなかったことを示す例である。',
    },
    year: 1585,
    keywords: ['関白', '朝廷の官', '豊臣秀吉', '関白相論', '惣無事', '北政所'],
    caveat:
      '「惣無事令」という単一の法令があったとする理解には批判があり、現在は個別の命令の集積とみる見方が有力です。秀吉が将軍ではなく関白を選んだ理由も諸説あり、足利義昭の猶子になろうとして断られたという話は後世の記録によるもので、確かな史料はありません。',
    gameNote:
      '盤上では他家の軍略が1減ります。これは私戦を禁じる命令によって他家の動きが縛られたことを表した処理で、命令が実際に全国で守られたわけではありません。従わなかった大名に対しては、九州や小田原へ大軍が向けられています。',
  },
};

/** 律令の官職の最高位。適任者がなければ欠員とする「則闕の官」。 */
export const DAIJOUDAIJIN: Card = {
  id: 'card:kani:daijoudaijin',
  name: '太政大臣',
  reading: 'だじょうだいじん',
  type: 'kani',
  cost: { zeni: 8 },
  timing: 'council',
  effects: [
    { t: 'gain', res: 'ishin', n: 4 },
    { t: 'draw', n: 1 },
  ],
  vp: 4,
  availableFrom: 4,
  note: {
    summary:
      '律令の官職の最高位。ふさわしい人がいなければ置かない「則闕の官」とされた。1586年に豊臣秀吉が任じられ、あわせて豊臣の姓を与えられている。',
    detail: {
      1: '太政大臣は、朝廷の役のなかでいちばん上の位です。ふさわしい人がいないときは、だれも任じないことになっていました。1586年に豊臣秀吉がこの役につきました。位をもらうときには朝廷にたくさんのお金や品物をおくるのがふつうで、公家たちのくらしはそれで支えられていました。',
      2: '太政大臣は律令制の官職の最高位で、適任者がなければ欠員としてよい「則闕の官」とされた。平清盛や足利義満が任じられた例がある。1586年（天正14年）12月、豊臣秀吉が任官し、同年9月には正親町天皇から豊臣の姓を賜っている。織田信長は1578年に右大臣・右近衛大将を辞して以後は無官で、1582年に朝廷から征夷大将軍・関白・太政大臣のいずれかへの任官を打診されたが（三職推任）、答えが記録に残らないまま本能寺の変で没し、没後に正一位・太政大臣を追贈された。官位の授与には朝廷への献金が伴い、それが公家社会の財源にもなっていた。',
      3: '太政大臣は律令官制における太政官の最高位で、「其の人なければ則ち闕く」とされたことから則闕の官と呼ばれ、適任者を欠く場合は置かないのが建前であった。実務の長は左大臣であり、太政大臣は名誉的な色彩が濃い。武家では平清盛、足利義満、のちに豊臣秀吉、徳川家康・秀忠らが任じられている（足利義教は生前は左大臣にとどまり、太政大臣は嘉吉元年（1441年）の死後に贈られたものである）。天正14年（1586年）9月、羽柴秀吉は正親町天皇から豊臣の姓を賜り、同年12月に太政大臣に任じられた。関白（1585年）に続くこの任官により、秀吉は朝廷の官位の頂点と摂関の地位を併せ持つことになる。一方、織田信長は天正6年（1578年）に右大臣・右近衛大将を辞して以後は官職を持たず、天正10年（1582年）5月、朝廷は安土の信長へ使者を送り、征夷大将軍・関白・太政大臣のいずれかに任じる用意があると伝えた。これを三職推任問題という。信長がどの職を望んだか、あるいはいずれも受ける気がなかったかは、6月2日の本能寺の変によって不明のまま残された。推任を朝廷側の主導とみるか、信長側の働きかけとみるかについても研究者の見解は分かれる。信長は没後まもなく正一位・太政大臣を追贈されている。なお、こうした任官には朝廷への献金や献上が随伴するのが通例で、応仁の乱以降に所領収入を失った公家社会にとって、官位の授与は重要な財源であった。官位の授受は権威と経済が結びついた制度であり、大名の側から見れば銭で買える権威、朝廷の側から見れば売ることのできる唯一の資産だった。',
    },
    year: 1586,
    keywords: ['太政大臣', '朝廷の官', '則闕の官', '三職推任', '豊臣秀吉', '織田信長'],
    caveat:
      '三職推任について、信長がどう応じるつもりだったかを示す史料はありません。朝廷が主導したのか信長側が求めたのかについても説が分かれており、これを本能寺の変と結びつける議論も推測の域を出ません。',
    gameNote:
      '盤上では札を1枚引けます。任官にともなう贈答と取次が人と物のつながりを広げたことを、手札の増加に置き換えた処理です。太政大臣の職務そのものに札を引く力があったわけではありません。',
  },
};

// ---------------------------------------------------------------------------
// 武家の棟梁
// ---------------------------------------------------------------------------

/** 武家の棟梁を示す職。1573年の時代法で山札から取り除かれる。 */
export const SEII_TAISHOUGUN: Card = {
  id: 'card:kani:seii_taishougun',
  name: '征夷大将軍',
  reading: 'せいいたいしょうぐん',
  type: 'kani',
  cost: { zeni: 10 },
  timing: 'council',
  effects: [
    { t: 'gain', res: 'ishin', n: 4 },
    { t: 'target', who: 'others', then: [{ t: 'lose', res: 'ishin', n: 1 }] },
  ],
  vp: 5,
  availableFrom: 1,
  note: {
    summary:
      'もとは蝦夷征討のための臨時の職だが、源頼朝以後は武家の棟梁を示す職となった。室町期は足利氏が世襲し、1603年に徳川家康が任じられて江戸幕府が開かれる。',
    detail: {
      1: '征夷大将軍は、もともと東北へ兵を出すときの臨時の役でしたが、源頼朝のあとは武士のかしらをあらわす役になりました。室町時代は足利氏がつぎました。1573年に足利義昭が京から追い出され、1603年には徳川家康がこの役につきました。',
      2: '征夷大将軍は本来、蝦夷征討のために置かれた臨時の官職だったが、源頼朝が任じられて以降、武家の棟梁を示す職となった。室町幕府では足利氏が世襲したが、戦国期の将軍は畿内の情勢に左右され、明応の政変（1493年）以降は二人の将軍候補が並び立つ状態が続き、1565年には足利義輝が三好方に襲われて死去した。1573年、織田信長が足利義昭を京から追放し、室町幕府の政治的機能は失われる。ただし義昭は将軍職のまま鞆へ移り、辞したのは1588年である。1603年、徳川家康が征夷大将軍に任じられ、江戸幕府が開かれた。',
      3: '征夷大将軍は、律令国家が東北への軍事行動のために設けた臨時の官職に始まる。建久3年（1192年）に源頼朝が任じられて以後、武家の棟梁を示す職としての性格が定着した。室町幕府では足利氏が世襲したが、戦国期の将軍権力は畿内の政治情勢に強く規定される。明応2年（1493年）の明応の政変で管領細川政元が足利義材を廃して義澄を立てて以降、二人の将軍候補が並び立つ状態が続き、永禄8年（1565年）には足利義輝が三好三人衆らに襲われて死去した。永禄11年（1568年）に織田信長に擁されて上洛した足利義昭は、やがて信長と対立し、元亀4年／天正元年（1573年）に京都を追われる。一般にこれをもって室町幕府の滅亡とするが、義昭は将軍職を保持したまま毛利氏を頼って鞆に移り、諸大名へ御内書を発し続けた。義昭が将軍職を辞して出家するのは天正16年（1588年）であり、「1573年に幕府が滅んだ」という区切りは制度上の終わりというより政治的機能の喪失を指す便宜的なものである。慶長8年（1603年）2月12日、徳川家康は右大臣に任じられると同日に征夷大将軍の宣下を受けた。これをもって江戸開府とするのが通例だが、この時点で豊臣秀頼は依然として高い官位を保っており、将軍宣下が直ちに全国支配の完成を意味したわけではないという指摘がある。将軍職が武家の頂点として疑いなく機能するのは、大坂の陣（1614-15年）を経てからのことである。',
    },
    year: [1467, 1603],
    keywords: ['征夷大将軍', '幕府の職', '足利義昭', '徳川家康', '室町幕府', '江戸幕府'],
    caveat:
      '「1573年に室町幕府が滅亡した」という区切りは便宜的なものです。足利義昭は追放後も将軍職を保持し、1588年に辞するまで諸大名へ御内書を出し続けました。1603年の将軍宣下をもって全国支配が完成したとみる理解にも、豊臣秀頼の官位の高さを根拠とする異論があります。',
    gameNote:
      'docs/01 §7 の時代法により、この札は1573年（第III章）に山札から取り除かれます。実際には足利義昭が将軍職にあったのは1588年までで、区切り方はゲームの都合によるものです。また他家の威信を1減らす効果は権威が一極に集まることを表した処理であり、将軍が実際に他の大名の威信を奪えたという意味ではありません。',
  },
};

// ---------------------------------------------------------------------------
// まとめ
// ---------------------------------------------------------------------------

/** 官位カード8枚。京（上洛）で購入する。すべて天下点（vp）を持つ。 */
export const KANI_CARDS: readonly Card[] = [
  // 朝廷の官 — 名乗り
  ZURYOUMEI,
  // 幕府の職 — 在地の統治
  SHUGODAI,
  SHUGOSHIKI,
  // 幕府の職 — 広域の出先
  TANDAI,
  KANTOU_KANREI,
  // 朝廷の官 — 頂点
  KANPAKU,
  DAIJOUDAIJIN,
  // 武家の棟梁
  SEII_TAISHOUGUN,
];
